import { useRawFetchState, Context } from '@vpap_svelte/core';
import { TooltipState } from '@vpap_svelte/charts';

const chambers = ['All', 'House of Delegates', 'State Senate'];

/**
 * @param {import('./types.d.ts').DataRecord[]} records
 * @returns {Map<string, import('./types.d.ts').LegislatorEntry[]>}
 */
function formatData(records) {
  /** @type {Map<string, Map<string, import('./types.d.ts').DataRecord[]>>} */
  const byChamber = new Map();

  for (const record of records) {
    if (!byChamber.has(record.chamber)) {
      byChamber.set(record.chamber, new Map());
    }
    const byLegislator = byChamber.get(record.chamber);
    if (!byLegislator.has(record.legislator)) {
      byLegislator.set(record.legislator, []);
    }
    byLegislator.get(record.legislator).push(record);
  }

  /** @type {Map<string, import('./types.d.ts').LegislatorEntry[]>} */
  const result = new Map();

  for (const [chamber, byLegislator] of byChamber) {
    /** @type {import('./types.d.ts').LegislatorEntry[]} */
    const entries = [];
    for (const [legislator, bills] of byLegislator) {
      if (bills.length <= 1) continue;
      entries.push({ legislator, party: bills[0].party, bills });
    }
    entries.sort(sorter);
    result.set(chamber, entries);
  }

  return result;
}

/** 
 * @param {import("./types.d.ts").LegislatorEntry} a 
 * @param {import("./types.d.ts").LegislatorEntry} b
 */
function sorter(a, b) {
  if (b.bills.length !== a.bills.length) return b.bills.length - a.bills.length;
  const lastA = a.legislator.split(' ').at(-1);
  const lastB = b.legislator.split(' ').at(-1);
  return lastA.localeCompare(lastB);

}

export class AppData {
  activeChamber = $state(chambers[0]);
  isTouchDevice = $state(false);

  /** @type {import('@vpap_svelte/core').FetchState<Map<string, import('./types.d.ts').LegislatorEntry[]>, any> | null} */
  #fetchState = null;

  /** @type {Map<string, import('./types.d.ts').LegislatorEntry[]> | null} */
  #staticData = $state.raw(null);

  /**
   * @type {TooltipState<import('./types.d.ts').DataRecord>}
   */
  tooltip;

  /**
   * @param {{ url?: string, data?: import('./types.d.ts').DataRecord[] }} options
   */
  constructor({ url, data } = {}) {
    if (data !== undefined) {
      this.#staticData = formatData(data);
    } else if (url !== undefined) {
      this.#fetchState = useRawFetchState(url, {}, {
        transformSuccess: async (res) => {
          const json = await res.json();
          console.log({ json })
          return formatData(json);
        }
      });
    }
    this.tooltip = new TooltipState();
  }

  get chambers() {
    return chambers;
  }

  /**
   * @returns {import("./types").LegislatorEntry[]}
   */
  get activeData() {
    const map = this.#fetchState ? this.#fetchState.data ?? new Map() : this.#staticData;
    if (this.activeChamber === "All") {
      let results = [];
      for (const chamberVals of map.values()) {
        results = results.concat(chamberVals);
      }
      results.sort(sorter);
      return results;
    }
    return map?.get(this.activeChamber) ?? [];
  }
}

/** @type {Context<AppData>} */
export const AppContext = new Context('AppData');
